import { useEffect, useState } from "react";
import { Info, Loader2, Users } from "lucide-react";
import { errorMessage, getStepImpact, type CampaignDetail, type StepImpact } from "@/api/email";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { impactLines } from "@/lib/email";

/**
 * Before a step is added to an active sequence: who it will reach, and that
 * it reaches nobody retroactively. `position` null = closed.
 */
export function StepImpactDialog({
  campaign,
  position,
  onOpenChange,
  onContinue,
}: {
  campaign: CampaignDetail;
  position: number | null;
  onOpenChange: (open: boolean) => void;
  onContinue: (position: number) => void;
}) {
  const open = position !== null;
  const [impact, setImpact] = useState<StepImpact | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (position === null) return;
    let cancelled = false;
    setImpact(null);
    setError(null);
    getStepImpact(campaign.project_slug, campaign.id, position)
      .then((result) => !cancelled && setImpact(result))
      .catch((err) => !cancelled && setError(errorMessage(err, "The impact could not be computed.")));
    return () => {
      cancelled = true;
    };
  }, [campaign.project_slug, campaign.id, position]);

  const atEnd = position === null || position > campaign.steps.length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg gap-5">
        <DialogHeader>
          <DialogTitle>{atEnd ? `Add step ${campaign.steps.length + 1}` : `Insert a step at position ${position}`}</DialogTitle>
          <DialogDescription>
            This campaign is active: a new step only reaches the contacts who haven&apos;t passed it yet.
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <p className="rounded-xl bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{error}</p>
        ) : !impact ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Counting the contacts…
          </div>
        ) : (
          <div className="space-y-3">
            <ul className="space-y-1.5 rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
              {impactLines(impact).map((line, index) => (
                <li key={line} className={index === 0 ? "flex items-center gap-2 font-medium text-slate-900" : "pl-6"}>
                  {index === 0 && <Users className="h-4 w-4 text-indigo-500" />}
                  {line}
                </li>
              ))}
            </ul>
            {!impact.allowed && impact.reason ? (
              <p className="rounded-xl bg-amber-50 px-3.5 py-2.5 text-sm text-amber-900">{impact.reason}</p>
            ) : (
              <p className="flex gap-2 text-xs leading-relaxed text-slate-500">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                At the next sync, Prism writes each of these contacts their own email for it before Lemlist can send
                it; a contact whose email can&apos;t be written is paused until it can. Contacts past this point,
                done or stopped never get it.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!impact?.allowed || position === null}
            onClick={() => position !== null && onContinue(position)}
          >
            Add step
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
